import Barcode from "assets/img/ecommerce/Code-128.png";
import { FiUser, FiPhone, FiMail, FiMapPin, FiCreditCard, FiCalendar, FiHash, FiDollarSign, FiBookOpen, FiClock, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

const Details = ({ orderData }) => {
  console.log("Details component received orderData:", orderData);
  
  const { 
    user, 
    paymentMethod, 
    paymentDate, 
    accountReference, 
    transactionId,
    amount,
    currency,
    courseTitle,
    subscriptionType,
    status,
    createdAt,
    phone
  } = orderData || {};

  // Format currency display
  const formatCurrency = (amount, currency) => {
    if (currency === 'USD') {
      return `$${amount.toFixed(2)}`;
    }
    return `Kshs. ${amount.toFixed(2)}`;
  };

  // Get status color and icon
  const getStatusInfo = (status) => {
    switch (status) {
      case 'active':
        return { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', icon: <FiCheckCircle className="w-5 h-5" /> };
      case 'pending':
        return { color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', icon: <FiClock className="w-5 h-5" /> };
      case 'expired':
        return { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: <FiAlertCircle className="w-5 h-5" /> };
      case 'canceled':
        return { color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', icon: <FiAlertCircle className="w-5 h-5" /> };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', icon: <FiAlertCircle className="w-5 h-5" /> };
    }
  };

  // Get payment method color and icon
  const getPaymentMethodInfo = (method) => {
    switch (method) {
      case 'Mpesa':
        return { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', icon: '🟢' };
      case 'WaafiPay':
        return { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: '🔵' };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', icon: '⚪' };
    }
  };

  const statusInfo = getStatusInfo(status);
  const paymentInfo = getPaymentMethodInfo(paymentMethod);

  return (
    <div className="w-full space-y-8">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-navy-700 dark:text-white mb-2">
          Order Details
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          Complete information about this subscription order
        </p>
        <div className="w-24 h-1 bg-gradient-to-r from-brand-500 to-orange-500 mx-auto mt-4 rounded-full"></div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        
        {/* Customer Information Card */}
        <div className="xl:col-span-1">
          <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-orange-500 to-brand-600 px-6 py-4">
              <div className="flex items-center">
                <FiUser className="text-white text-xl mr-3" />
                <h3 className="text-white text-lg font-bold">Customer Information</h3>
              </div>
            </div>
            
            {/* Card Content */}
            <div className="p-6 space-y-6">
              {/* Customer Avatar & Name */}
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-brand-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </span>
                </div>
                <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                  {user?.firstName} {user?.lastName}
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Customer
                </p>
              </div>
              
              {/* Contact Details */}
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <FiMail className="text-brand-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Email Address</p>
                    <a
                      className="text-navy-700 dark:text-white font-medium hover:text-brand-500 transition-colors"
                      href={`mailto:${user?.email}`}
                    >
                      {user?.email || "N/A"}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <FiPhone className="text-brand-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Phone Number</p>
                    <p className="text-navy-700 dark:text-white font-medium">
                      {user?.phoneNumber || phone || "N/A"}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <FiMapPin className="text-brand-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Location</p>
                    <p className="text-navy-700 dark:text-white font-medium">
                      {user?.city || "N/A"}, {user?.country || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Information Card */}
        <div className="xl:col-span-1">
          <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
              <div className="flex items-center">
                <FiCreditCard className="text-white text-xl mr-3" />
                <h3 className="text-white text-lg font-bold">Payment Information</h3>
              </div>
            </div>
            
            {/* Card Content */}
            <div className="p-6 space-y-6">
              {/* Payment Method Badge */}
              <div className="text-center">
                <div className={`inline-flex items-center px-4 py-2 rounded-full ${paymentInfo.bg} ${paymentInfo.border} mb-4`}>
                  <span className="text-2xl mr-2">{paymentInfo.icon}</span>
                  <span className={`font-bold ${paymentInfo.color}`}>
                    {paymentMethod === 'Mpesa' ? 'M-Pesa' : paymentMethod}
                  </span>
                </div>
              </div>
              
              {/* Amount */}
              <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Amount Paid</p>
                <p className="text-3xl font-bold text-navy-700 dark:text-white">
                  {formatCurrency(amount, currency)}
                </p>
              </div>
              
              {/* Payment Details */}
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <FiHash className="text-blue-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">System Reference</p>
                    <p className="text-navy-700 dark:text-white font-mono text-sm font-medium">
                      {accountReference}
                    </p>
                  </div>
                </div>
                
                {transactionId && transactionId !== 'N/A' && (
                  <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                    <FiDollarSign className="text-blue-500 mr-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Transaction ID</p>
                      <p className="text-navy-700 dark:text-white font-mono text-sm font-medium">
                        {transactionId}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <FiCalendar className="text-blue-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Payment Date</p>
                    <p className="text-navy-700 dark:text-white font-medium">
                      {new Date(paymentDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Information Card */}
        <div className="xl:col-span-1">
          <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
              <div className="flex items-center">
                <FiBookOpen className="text-white text-xl mr-3" />
                <h3 className="text-white text-lg font-bold">Course Information</h3>
              </div>
            </div>
            
            {/* Card Content */}
            <div className="p-6 space-y-6">
              {/* Course Title */}
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiBookOpen className="text-white text-xl" />
                </div>
                <h4 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
                  {courseTitle}
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Online Course
                </p>
              </div>
              
              {/* Course Details */}
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <FiCalendar className="text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Subscription Type</p>
                    <p className="text-navy-700 dark:text-white font-medium capitalize">
                      {subscriptionType}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <div className="mr-3 flex-shrink-0">
                    {statusInfo.icon}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bg} ${statusInfo.border} ${statusInfo.color}`}>
                      {status === 'active' ? 'Active' : status === 'pending' ? 'Pending' : status}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                  <FiClock className="text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Created Date</p>
                    <p className="text-navy-700 dark:text-white font-medium">
                      {new Date(createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Barcode and Summary */}
      <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg border border-gray-100 dark:border-navy-700 p-8">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          {/* Order Summary */}
          <div className="lg:flex-1 mb-6 lg:mb-0">
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Order Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                <FiHash className="text-brand-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Order ID</p>
                  <p className="text-navy-700 dark:text-white font-mono font-medium">
                    #{orderData?.id}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-gray-50 dark:bg-navy-700 rounded-lg">
                <FiDollarSign className="text-brand-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Amount</p>
                  <p className="text-navy-700 dark:text-white font-bold">
                    {formatCurrency(amount, currency)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Barcode */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-xl shadow-md border border-gray-200">
              <img src={Barcode} className="h-20 w-20" alt="barcode" />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Order Barcode
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Details;
